import { useLayoutEffect, useRef, useState } from 'react';

function getRowTop(nodes, rowIndex) {
  if (!nodes.length) return null;
  const firstTop = nodes[0].offsetTop;
  if (rowIndex <= 0) return firstTop;

  const tops = [];
  for (let i = 0; i < nodes.length; i += 1) {
    const top = nodes[i].offsetTop;
    if (!tops.length || tops[tops.length - 1] !== top) tops.push(top);
  }

  return tops[Math.min(rowIndex, tops.length - 1)];
}

export function useRowOverflow(items, { maxRows, identityKey } = {}) {
  const rows = Math.max(1, Number(maxRows) || 1);
  const measureRef = useRef(null);
  const itemRefs = useRef([]);
  const overflowRef = useRef(null);
  const rafRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const itemsKey = identityKey != null ? identityKey : items.join('|');

  const setItemRef = (index) => (el) => {
    itemRefs.current[index] = el;
  };

  useLayoutEffect(() => {
    const measureContainer = measureRef.current;
    if (!measureContainer) return;

    const compute = () => {
      const nodes = itemRefs.current.filter(Boolean);
      const overflowNode = overflowRef.current;
      if (!nodes.length) {
        setVisibleCount(0);
        return;
      }

      const maxAllowedTopIndex = rows - 1;

      if (overflowNode) overflowNode.style.display = 'none';
      for (let i = 0; i < nodes.length; i += 1) {
        nodes[i].style.display = '';
      }

      const allowedTopNoOverflow = getRowTop(nodes, maxAllowedTopIndex);
      let allFit = true;
      for (let i = 0; i < nodes.length; i += 1) {
        if (nodes[i].offsetTop > allowedTopNoOverflow) {
          allFit = false;
          break;
        }
      }

      if (allFit) {
        setVisibleCount(items.length);
        return;
      }

      if (!overflowNode) {
        setVisibleCount(Math.max(0, items.length - 1));
        return;
      }

      overflowNode.style.display = '';

      const fitsWithK = (k) => {
        const hidden = Math.max(0, items.length - k);
        overflowNode.textContent = `+${hidden}`;

        for (let i = 0; i < nodes.length; i += 1) {
          nodes[i].style.display = i < k ? '' : 'none';
        }

        const visibleNodes = nodes.slice(0, k);
        if (!visibleNodes.length) return true;

        const layoutNodes = [...visibleNodes, overflowNode];
        const allowedTop = getRowTop(layoutNodes, maxAllowedTopIndex);
        for (let i = 0; i < layoutNodes.length; i += 1) {
          if (layoutNodes[i].offsetTop > allowedTop) return false;
        }

        return true;
      };

      let k = items.length - 1;
      while (k > 0 && !fitsWithK(k)) k -= 1;
      setVisibleCount(k);
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(compute);

    const observer = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(compute);
    });
    observer.observe(measureContainer);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      observer.disconnect();
    };
  }, [items.length, itemsKey, rows]);

  const hasOverflow = visibleCount < items.length;
  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);
  const hiddenCount = hiddenItems.length;

  return {
    measureRef,
    setItemRef,
    overflowRef,
    visibleItems,
    hasOverflow,
    hiddenCount,
    hiddenItems,
  };
}
